import { Component, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SignBoxComponent } from '@libs/charting';
import { imageMimeTypes } from '@libs/utils';
import { PreviewImageModule } from '@libs/media-preview';
import { ToastService } from '@libs/shared-services';
import { SiteSetupComponentService } from '../../../site-setup/site-setup.component.service';

@Component({
  selector: 'lib-license-agreement-sign-box',
  imports: [CommonModule, SvgIconComponent, TranslateModule, SignBoxComponent, PreviewImageModule],
  templateUrl: './license-agreement-sign-box.component.html',
  styleUrl: './license-agreement-sign-box.component.scss',
})
export class LicenseAgreementSignBoxComponent {
  private readonly siteSetupComponentService = inject(SiteSetupComponentService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  public vm = this.siteSetupComponentService.viewModel;

  public isSigned = false;
  public imageUrl: string | null = null;
  public isDragging = signal(false);
  protected acceptedMimeTypes = [imageMimeTypes];

  @ViewChild('imageUploader', { static: true, read: ElementRef }) private readonly imageUploaderElement!: ElementRef<HTMLInputElement>;

  protected onSignChange(signBlob: Blob | null): void {
    this.siteSetupComponentService.signForm(signBlob);
  }

  protected onSigned(signed: boolean): void {
    if (signed) {
      this.isSigned = true;
    } else {
      this.isSigned = false;
      this.siteSetupComponentService.signForm(null);
    }
  }

  protected onClearClick(event: MouseEvent): void {
    event.stopPropagation();

    this.clearImage();
  }

  private clearImage(): void {
    this.imageUrl = null;
    this.siteSetupComponentService.signForm(null);
    this.imageUploaderElement.nativeElement.value = '';
  }

  protected onUploadClick(): void {
    if (this.imageUrl === null) {
      this.imageUploaderElement.nativeElement.click();
    }
  }

  protected onUpload(event: Event | DragEvent): void {
    let files: FileList | null = null;

    if (event instanceof DragEvent) {
      files = event.dataTransfer?.files || null;
    } else if (event instanceof Event) {
      const input = event.target as HTMLInputElement;
      files = input.files || null;
    }

    if (!files?.length) {
      return;
    }

    if (files[0].type !== 'image/png') {
      this.clearImage();
      this.toastService.warning(this.translateService.instant('site_setup_contract_upload_file_validation'));
      return;
    }

    const url = URL.createObjectURL(files[0]);
    this.imageUrl = url;
    this.siteSetupComponentService.signForm(files[0]);
    this.imageUploaderElement.nativeElement.value = '';
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isDragging.set(true);
  }

  protected onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isDragging.set(false);
  }

  protected onDrop(event: DragEvent): void {
    this.onDragLeave(event);
    this.onUpload(event);
  }
}
